import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, tokenManager } from '../utils/api';

// Query keys
const WHAT_WE_OFFER_ABOUT_QUERY_KEY = 'whatWeOfferAbout';

// Custom hook for fetching what-we-offer about data
export const useWhatWeOfferAbout = (page = 1, limit = 10) => {
  return useQuery({
    queryKey: [WHAT_WE_OFFER_ABOUT_QUERY_KEY, page, limit],
    queryFn: () => apiClient.get('what-we-offer/about-page', { page, limit }),
    keepPreviousData: true, // Keep previous data while fetching new data
    staleTime: 2 * 60 * 1000, // 2 minutes
    select: (data) => {
      // Transform the API response to match the expected format
      return {
        data: data.data || [],
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        has_next: data.has_next || false,
        has_prev: data.has_prev || false,
        pagination: {
          current: data.page || 1,
          pageSize: data.limit || 10,
          total: data.total || 0
        }
      };
    }
  });
};

// Custom hook for creating what-we-offer about data
export const useCreateWhatWeOfferAbout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => apiClient.put('what-we-offer/about-page', data),
    onSuccess: () => {
      // Invalidate and refetch what-we-offer about data
      queryClient.invalidateQueries({ queryKey: [WHAT_WE_OFFER_ABOUT_QUERY_KEY] });
    },
  });
};

// Custom hook for updating what-we-offer about data
export const useUpdateWhatWeOfferAbout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`what-we-offer/about-page/${id}`, data),
    onSuccess: () => {
      // Invalidate and refetch what-we-offer about data
      queryClient.invalidateQueries({ queryKey: [WHAT_WE_OFFER_ABOUT_QUERY_KEY] });
    },
  });
};

// Custom hook for deleting what-we-offer about data
export const useDeleteWhatWeOfferAbout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => apiClient.delete(`what-we-offer/about-page/${id}`),
    onSuccess: () => {
      // Invalidate and refetch what-we-offer about data
      queryClient.invalidateQueries({ queryKey: [WHAT_WE_OFFER_ABOUT_QUERY_KEY] });
    },
  });
};

// Custom hook for getting a single what-we-offer about item
export const useWhatWeOfferAboutItem = (id) => {
  return useQuery({
    queryKey: [WHAT_WE_OFFER_ABOUT_QUERY_KEY, id],
    queryFn: () => apiClient.get(`what-we-offer/about-page/${id}`),
    enabled: !!id, // Only run query if id exists
  });
}; 